import React from 'react';
import { AdrenalGauge } from '../../models/StressAdrenalAnalysis';

// Bidirectional Na/K dial shaped like the reference bar: GREEN in the middle,
// severity colors fanning out to BOTH ends. Low end = adrenal insufficiency,
// high end = adrenal excess. Deliberately NOT a probability gauge (no 0–100%
// sweep) so it doesn't read like the glucose likelihood dial.

// Bar fills (same palette as the results-page marker bars / other gauges).
const FILL = { green: '#78F880', yellow: '#FEFA83', orange: '#FFD37E', red: '#FC6552' };
// The category word + ratio value use the same blue as the glucose gauge label.
const LABEL_BLUE = '#337ab7';

// Symmetric 7-band arc (mirror of the breakpoints in StressAdrenalAnalysis).
const STOPS: Array<[number, number, string]> = [
  [0, 12, FILL.red],
  [12, 28, FILL.orange],
  [28, 43, FILL.yellow],
  [43, 57, FILL.green],
  [57, 72, FILL.yellow],
  [72, 88, FILL.orange],
  [88, 100, FILL.red],
];

const W = 280;
const CX = 140;
const CY = 140;
// Band thickness matches the glucose gauge (Highcharts solidgauge uses
// innerRadius 60% / outerRadius 100% → the band is 40% of the radius).
const OUTER_R = 120;
const INNER_R = 72; // 60% of OUTER_R
const MID_R = (OUTER_R + INNER_R) / 2; // arc midline — the stroke is centered here
const STROKE = OUTER_R - INNER_R; // 40% of OUTER_R
// Needle (dial) — matched to the Highcharts solidgauge dial the other gauges
// use: a thin black needle tapering from a 3px base to a 1px tip, reaching 80%
// of the pane radius, with a short rear stub behind a 5px pivot. These are the
// Highcharts default dial/pivot dimensions expressed against OUTER_R.
const DIAL_RADIUS = OUTER_R * 0.8; // 80% of pane radius
const DIAL_BASE_LEN = OUTER_R * 0.7; // 70% — base width holds out to here, then tapers
const DIAL_REAR = OUTER_R * 0.1; // 10% rear stub behind the pivot
const DIAL_BASE_W = 3;
const DIAL_TOP_W = 1;
const DIAL_PIVOT_R = 5;
const DIAL_COLOR = '#000';
// Outer-edge scale ticks: a short radial mark just past the band, with the Na/K
// value sitting a little further out. Headroom is added to the viewBox top so
// the labels near 12 o'clock don't clip; the bottom is padded for the two-line
// side labels that sit under each red band.
const TICK_LEN = 5;
const LABEL_R = OUTER_R + 15;
const TOP_PAD = 14;
const BOTTOM_PAD = 38;
const VB_TOP = -TOP_PAD;
const SVG_H = 150 + TOP_PAD + BOTTOM_PAD;
// Tick colors. Every number label renders in TICK_STRONG (bold) so the whole
// scale reads uniformly; TICK_MUTED only lightens the lab/alarm tick MARKS so
// the optimal-range marks still stand out a touch.
const TICK_STRONG = '#222';
const TICK_MUTED = '#aaa';
// Caption baselines sit low enough that the dial's rear stub (which reaches
// ~12px below the pivot when the needle is near vertical) clears the center
// "Adrenal Function" text.
const SIDE_LABEL_Y1 = 167;
const SIDE_LABEL_Y2 = 180;

const polar = (v: number, radius: number) => {
  const a = ((180 - 1.8 * v) * Math.PI) / 180; // v=0 → left, v=50 → top, v=100 → right
  return { x: CX + radius * Math.cos(a), y: CY - radius * Math.sin(a) };
};

// Tapered Highcharts-style dial as an SVG polygon. Built in a local frame where
// the needle points along +x (pivot at origin), then rotated to the needle
// angle and translated to the pivot — mirrors how Highcharts renders its dial.
const dialPolygon = (v: number): string => {
  const a = ((180 - 1.8 * v) * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const hb = DIAL_BASE_W / 2;
  const ht = DIAL_TOP_W / 2;
  const local: Array<[number, number]> = [
    [-DIAL_REAR, -hb],
    [DIAL_BASE_LEN, -hb],
    [DIAL_RADIUS, -ht],
    [DIAL_RADIUS, ht],
    [DIAL_BASE_LEN, hb],
    [-DIAL_REAR, hb],
  ];
  return local
    .map(([lx, ly]) => `${(CX + lx * cos + ly * sin).toFixed(1)},${(CY - lx * sin + ly * cos).toFixed(1)}`)
    .join(' ');
};

// Side labels sit centered under the MIDDLE of each red band (needle 6 / 94),
// so they read as the captions for the two severity extremes.
const LEFT_RED_CX = polar(6, MID_R).x;
const RIGHT_RED_CX = polar(94, MID_R).x;

const arc = (v1: number, v2: number, color: string, key: string) => {
  const p1 = polar(v1, MID_R);
  const p2 = polar(v2, MID_R);
  return (
    <path
      key={key}
      d={`M ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} A ${MID_R} ${MID_R} 0 0 1 ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`}
      stroke={color}
      strokeWidth={STROKE}
      fill="none"
    />
  );
};

type Props = { state: AdrenalGauge; valueLabel?: string };

export class StressAdrenalGauge extends React.Component<Props, {}> {
  render() {
    const { state, valueLabel } = this.props;

    return (
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={SVG_H} viewBox={`0 ${VB_TOP} ${W} ${SVG_H}`} aria-hidden="true" style={{ maxWidth: '100%' }}>
          {STOPS.map(([from, to, color], i) => arc(from, to, color, `seg-${i}`))}
          {state.ticks.map(t => {
            const a = polar(t.needle, OUTER_R);
            const b = polar(t.needle, OUTER_R + TICK_LEN);
            const lp = polar(t.needle, LABEL_R);
            const color = t.emphasis ? TICK_STRONG : TICK_MUTED;
            return (
              <g key={`tick-${t.needle}`}>
                <line
                  x1={a.x.toFixed(1)}
                  y1={a.y.toFixed(1)}
                  x2={b.x.toFixed(1)}
                  y2={b.y.toFixed(1)}
                  stroke={color}
                  strokeWidth={t.emphasis ? 1.6 : 1.2}
                />
                <text
                  x={lp.x.toFixed(1)}
                  y={lp.y.toFixed(1)}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={10.5}
                  fontWeight={700}
                  fill={TICK_STRONG}
                >
                  {t.label}
                </text>
              </g>
            );
          })}
          <polygon points={dialPolygon(state.needle)} fill={DIAL_COLOR} />
          <circle cx={CX} cy={CY} r={DIAL_PIVOT_R} fill={DIAL_COLOR} />

          {/* Three captions — low / balanced / high. The one matching the
              current condition is emphasized; the others read as gray context.
              The end captions sit under the middle of each red band; "Adrenal
              Function" sits at center under the green band / needle and pairs
              with the "is Balanced" line below. */}
          <text
            textAnchor="middle"
            fontSize="13.5"
            fontWeight={state.condition === 'insufficiency' ? 700 : 500}
            fill={state.condition === 'insufficiency' ? '#222' : '#bbb'}
          >
            <tspan x={LEFT_RED_CX.toFixed(1)} y={SIDE_LABEL_Y1}>Adrenal</tspan>
            <tspan x={LEFT_RED_CX.toFixed(1)} y={SIDE_LABEL_Y2}>Insufficiency</tspan>
          </text>
          {/* Center caption only appears in the balanced state — there's no
              "Adrenal Function" reading for the low/high ends. Sits at center
              under the needle, aligned with the side captions. */}
          {state.condition === 'balanced' && (
            <text textAnchor="middle" fontSize="13.5" fontWeight={700} fill="#222">
              <tspan x={CX} y={SIDE_LABEL_Y1}>Adrenal</tspan>
              <tspan x={CX} y={SIDE_LABEL_Y2}>Function</tspan>
            </text>
          )}
          <text
            textAnchor="middle"
            fontSize="13.5"
            fontWeight={state.condition === 'hyperfunction' ? 700 : 500}
            fill={state.condition === 'hyperfunction' ? '#222' : '#bbb'}
          >
            <tspan x={RIGHT_RED_CX.toFixed(1)} y={SIDE_LABEL_Y1}>Adrenal</tspan>
            <tspan x={RIGHT_RED_CX.toFixed(1)} y={SIDE_LABEL_Y2}>Excess</tspan>
          </text>
        </svg>

        {/* Likelihood category, centered. In the balanced state there is no
            dysfunction to be "Less Likely", so it reads "is Balanced" instead. */}
        <div style={{ marginTop: '8px', fontSize: '15px' }}>
          <span style={{ fontWeight: 400, color: '#000' }}>is</span>{' '}
          <span style={{ fontWeight: 700, color: LABEL_BLUE }}>
            {state.condition === 'balanced' ? 'Balanced' : state.category}
          </span>
        </div>

        {/* Na/K ratio value — "(Na/K Ratio = " and ")" black, the value blue */}
        {valueLabel != null && (
          <div style={{ marginTop: '2px', fontSize: '12.5px', color: '#000' }}>
            (Na/K Ratio = <span style={{ color: LABEL_BLUE, fontWeight: 700 }}>{valueLabel}%</span>)
          </div>
        )}
      </div>
    );
  }
}
